<template>
  <transition name="fade">
    <div v-if="post" data-cy="post-container" class="post">
      <div v-if="post.community" class="top">
        <community-link :community="post.community" class="community-title" />
        <AdminPostActionsMenu :post="post" v-if="isAdminPage" />
        <PostMenu :post="post" v-else />
      </div>
      <UserVignette v-if="post.author" :user="post.author" :bottom-slot="time()" />
      <div class="post-content">
        <h3 v-if="post.title" class="title" :class="reported ? 'reported-text' : ''">
          {{ post.title }}
        </h3>
        <v-image :image-id="post.idImage" class="post-visual" :clickable="true" />
        <div v-if="post.content" class="content-block" :class="reported ? 'reported-text' : ''">
          <div class="content" v-linkify v-mentionify="goToUserInfo" v-html="content" />
          <LinkMetadataCover v-if="post.metadata" :title="post.metadata.title" :description="post.metadata.description"
            :image="post.metadata.image" :url="post.metadata.url" />
        </div>
      </div>
      <div class="user-actions" v-if="!isAdminPage">
        <LikePostButton :post="post" />
        <bookmark-post :post="post" />
      </div>
      <div class="actions" v-if="!isAdminPage">
        <UserLikesModal :likes="post.likes" item-type="Post" :id-item="post.id" />
        <div class="show-attachments" v-if="enableDocument && post.documents && post.documents.length > 0"
          @click="toggleShowAttachments">
          <img src="@/assets/AttachementIcon.svg" alt="show-attachments-icon" />
          <span>{{ post.documents.length }} {{ $t('post.post.attachments') }}</span>
        </div>
        <div data-cy="show-comments" class="show-comments" @click="toggleShowComments">
          <img v-if="post.commented" src="@/assets/CommentIconFilled.svg" alt="comments-icon" />
          <img v-else src="@/assets/CommentIcon.svg" alt="comments-icon" />
          <span>{{ post.commentsCount }} {{ $t('post.post.comments') }}</span>
        </div>
      </div>
      <div class="attachments-container" v-show="showAttachments">
        <post-attachments :documents="post.documents" />
        <div class="attachment-title">
          {{ $t('post.post.attachments') }}
        </div>
      </div>
      <comments v-if="showComments" class="comments" :post-id="post.id" :enable-edit="!isAdminPage"
        @close-comments="toggleShowComments" :is-admin-page="isAdminPage" :community-id="post.community.id" />
    </div>
  </transition>
</template>

<script>
import CommunityLink from './CommunityLink'
import VImage from '../shared/VImage'
import PostAttachments from './PostAttachments'
import LikePostButton from '../like/LikePostButton'
import BookmarkPost from './BookmarkPost'
import Comments from './Comments/Comments.vue'
import linkify from 'vue-linkify'
import PostMenu from './PostMenu'
import { mapState } from 'vuex'
import config from '../../config'
import AdminPostActionsMenu from '../administrator/AdminPostActionsMenu'
import UserVignette from '../user/UserVignette'
import LinkMetadataCover from '../shared/LinkMetadataCover'
import UserLikesModal from '../like/UserLikesModal'

export default {
  name: 'Post',
  components: {
    PostMenu,
    Comments,
    LikePostButton,
    PostAttachments,
    VImage,
    CommunityLink,
    BookmarkPost,
    UserLikesModal,
    AdminPostActionsMenu,
    UserVignette,
    LinkMetadataCover
  },
  props: {
    post: {
      type: Object,
      required: true
    },
    isAdminPage: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  directives: {
    linkify
  },
  created() {
    if (this.isAdminPage && this.post.reportedCommentsCount > 0) {
      this.displayComments()
    }
    this.showAdminOptions = this.$store.getters.auth.isAdmin && this.$store.getters.activeRoute.name === 'administratorPage'
  },
  data() {
    return {
      showFullContent: false,
      showAttachments: false,
      showComments: false,
      showAdminOptions: false,
      enableDocument: config.ENABLE_DOCUMENT
    }
  },
  methods: {
    goToUserInfo(userId) {
      this.$router.push({ name: 'userInfo', params: { id: userId } })
    },
    time() {
      const createdAt = this.post.createdAt
      return this.$store.getters.getDateFromNow(createdAt)
    },
    seeMore() {
      this.showFullContent = true
    },
    toggleShowAttachments() {
      this.showAttachments = !this.showAttachments
    },
    async displayComments() {
      this.showComments = true
    },
    async toggleShowComments() {
      this.showComments = !this.showComments
    },
    goTo(page, id) {
      this.$router.push({ name: page, params: { id: id } })
    }
  },
  computed: {
    content() {
      return this.$sanitizeHTML(this.$decodeHTML(this.post.content))
      // return this.post.content
    },
    contentTooLarge() {
      return this.post.content && this.post.content.length > 200
    },
    truncatedContent() {
      return this.post.content.substring(0, 200).concat('...')
    },
    reported() {
      return this.showAdminOptions && this.post.reportCount > 0
    },
    ...mapState({
      comments: state => state.comment.comments
    })
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
$left-margin: 1rem;

.post {
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  box-shadow: 2px 2px 8px 0 rgba(69, 69, 69, 0.20);
  border-radius: 3px;
  width: calc(100% - 32px);
  max-width: 606px;
  margin: 0.6rem 1rem;
  border-top: solid 2px $secondary-color;
  text-align: left;
  color: $black-color;
  letter-spacing: 0;
  line-height: 24px;
  position: relative; // fix for IE for PostMenu.vue

  @media (max-width: $medium) {
    max-width: none;
  }

  @media (max-width: $header-change) {
    width: calc(100% - 1rem);
    max-width: 80vw;
    margin-left: 0.5rem;
  }

  @media (max-width: $small) {
    max-width: none;
  }
}

.top {
  display: flex;
  justify-content: space-between;
}

.community-title {
  color: $primary-color;
  font-weight: bold;
  font-size: 14px;
  line-height: 21px;
}

.post-content {
  line-height: 16px;
  padding: 0.8rem 0;

  &>*+* {
    margin-top: 0.8rem;
  }

  .title {
    margin: 0 $left-margin;
    font-weight: bold;
  }

  .post-visual {
    max-width: 100%;
    width: 100%;
    max-height: 20rem;
    object-fit: contain;
    background-color: #efefef;
    display: block;
  }

  .reported-text {
    color: $cerise-color;
  }

  .content-block {
    margin: 0 $left-margin;

    .content {
      overflow-wrap: break-word;
    }

    .more {
      display: flex;
      justify-content: flex-end;
      color: $primary-color;
      font-weight: 600;
      margin-right: 1rem;
      margin-top: -1.1rem;
      font-size: 16px;
      text-decoration: none;

      img {
        margin-top: 1px;
      }
    }
  }
}

.user-actions,
.actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
  padding: 5px $left-margin;
  border-top: 1px solid lightgray;
  justify-content: space-around;
}

.user-actions {
  justify-content: space-between;
}

.show-attachments,
.show-comments {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 0 $left-margin;
  cursor: pointer;

  img {
    color: aqua;
  }

  span {
    margin-left: 6px;
  }
}

.attachments-container {
  display: flex;
  justify-content: space-between;
  background: #F2F2F2;
  border-top: 1px solid lightgray;
  padding-bottom: 6px;
  width: 100%;

  .attachment-title {
    font-size: 28px;
    color: #D8D8D8;
    margin: 12px 15px 0 0;
  }
}
</style>
